import { useQuery } from 'react-query';
import { HiChevronRight } from 'react-icons/hi2';
import { useContext, useState } from 'react';
import { PageMapping } from '../../index';
import { Box } from '../../../../common/AtomicDesign/atoms';
import RelativeLoading from '../../../../common/AtomicDesign/RelativeLoading';
import { BuyCreditsContext } from '../../context';
import { fetchUserCredits, getBookCreditsList } from '../../../../../api';
import CreditOfferCard from '../CreditOfferCard';
import { handleHttpError } from '../../helper/errorHandling';

type Props = {
  setActivePage: (value: PageMapping) => void;
  isLoading: boolean;
};

const cardInfo = [
  {
    id: '1000-print-credits',
    credits: 1000,
    price: 0,
    perAmount: 3.98,
    totalAmount: 3980,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 1000 Print Credits',
      chargebeeComponentId: '1000-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-1000-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '500-print-credits',
    credits: 500,
    price: 0,
    perAmount: 4.65,
    totalAmount: 2325,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 500 Print Credits',
      chargebeeComponentId: '500-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-500-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '300-print-credits',
    credits: 300,
    price: 0,
    perAmount: 5.55,
    totalAmount: 1665,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 300 Print Credits',
      chargebeeComponentId: '300-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-300-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '100-print-credits',
    credits: 100,
    price: 0,
    perAmount: 6.49,
    totalAmount: 649,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 100 Print Credits',
      chargebeeComponentId: '100-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-100-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '60-print-credits',
    credits: 60,
    price: 0,
    perAmount: 8.54,
    totalAmount: 512,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 60 Print Credits',
      chargebeeComponentId: '60-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-60-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '50-print-credits',
    credits: 50,
    price: 0,
    perAmount: 8,
    totalAmount: 400,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 50 Print Credits',
      chargebeeComponentId: '50-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-50-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '20-print-credits',
    credits: 20,
    price: 0,
    perAmount: 10,
    totalAmount: 200,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 20 Print Credits',
      chargebeeComponentId: '20-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-20-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
  {
    id: '8-print-credits',
    credits: 8,
    price: 0,
    perAmount: 12.5,
    totalAmount: 100,
    isActive: false,
    isLoading: false,
    type: 'book',
    product: {
      title: 'Authorify Book 8 Print Credits',
      chargebeeComponentId: '8-print-credits-USD',
      chargebeeProductHandle: 'books',
      chargebeeProductPriceHandle: 'original-8-print-credits',
      toShowBuyCredits: true,
      type: 'one_time',
    },
  },
];

const BuyCreditsOfferPage: React.FC<Props> = ({ setActivePage, isLoading }) => {
  const [bestDealId, setBestDealId] = useState<string | null>(null);
  const [fakeLoadingOnId, setFakeLoadingOnId] = useState(false);

  const {
    userCredits,
    setUserCredits,
    setBookCreditsList,
    bookCreditsList,
    setSelectedCreditsOffer,
  } = useContext(BuyCreditsContext);

  const {
    isLoading: isLoadingGetBookCreditsList,
    isRefetching: isRefetchingGetBookCreditsList,
  } = useQuery('getBookCreditsList', () => getBookCreditsList({ type: 'book' }), {
    onSuccess: (data) => {
      let minId = null;
      let minPerAmount = Infinity;

      data.forEach((item) => {
        if (item.perAmount < minPerAmount) {
          minPerAmount = item.perAmount;
          minId = item.id;
        }
      });

      setBestDealId(minId);
      setBookCreditsList(data.reverse() || []);
    },
    onError: (error) => handleHttpError(error),
  });

  const handleOfferSelection = (offer) => () => {
    setSelectedCreditsOffer(offer);
    setFakeLoadingOnId(offer.id);
    setTimeout(() => {
      setFakeLoadingOnId(null);
      setActivePage({
        id: 'checkout',
        title: 'Buy credits',
      });
    }, 300);
  };

  const {
    refetch: refetchUserCredits,
    isRefetching: isRefetchingUserCredits,
    isLoading: isLoadingUserCredits,
  } = useQuery('fetchUserCredits', fetchUserCredits, {
    onSuccess: (res) => {
      setUserCredits(res?.data?.data?.credits || 0);
    },
    onError: (error) => handleHttpError(error),
  });

  const navigateBack = () => {
    setActivePage({
      id: 'buy-credits',
      title: 'Buy credits',
    });
  };

  const handleOfferType = (item) => {
    if (item.credits === 1000) return 'best-value';
    if (item.credits === 100) return 'most-popular';
    return 'normal';
  };

  // Calculate savings percentage compared to the smallest package (highest price per credit)
  const basePrice = cardInfo.reduce((max, item) => Math.max(max, item.perAmount), 0);
  const calculateSavingsPercent = (perAmount: number) => {
    if (perAmount >= basePrice) return 0;
    return Math.round(((basePrice - perAmount) / basePrice) * 100);
  };

  // Featured cards: Best Value (1000) first, then Most Popular (100)
  const featuredCards = cardInfo
    .filter((item) => item.credits === 1000 || item.credits === 100)
    .sort((a, b) => b.credits - a.credits);
  // Regular cards: everything else, sorted by credits descending
  const regularCards = cardInfo
    .filter((item) => item.credits !== 1000 && item.credits !== 100)
    .sort((a, b) => b.credits - a.credits);

  return (
    <>
      <div id="breadcrumbs" className="flex gap-2 items-center font-figtree">
        <span className="cursor-pointer hover:bg-neutral-100" onClick={navigateBack}>
          Book catalog
        </span>
        <HiChevronRight />
        <span className="border-b-[1px] border-neutral-300">Credits</span>
      </div>

      <Box className="mt-6 relative font-figtree">
        <RelativeLoading
          isLoading={
            isLoadingGetBookCreditsList ||
            isRefetchingGetBookCreditsList ||
            isLoading ||
            isRefetchingUserCredits ||
            isLoadingUserCredits
          }
        />
        <div className="flex flex-col border-b-[1px] p-[22px] ps-6 pe-6">
          <span className="text-base font-semibold">You have {userCredits} credits</span>
          <span className="text-sm text-neutral-500">
            Use credits to buy printed books. 1 credit = 1 printed book. The more credits you get,
            the more you save.
          </span>
        </div>

        {/* Featured Deals Section */}
        <div className="p-6 pb-4">
          <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
            Recommended for you
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {featuredCards.map((item) => (
              <div key={item.id} className="flex flex-col">
                <CreditOfferCard
                  price={4}
                  id={item.id}
                  perAmount={item.perAmount}
                  totalAmount={item.totalAmount}
                  credits={item.credits}
                  isActive={item.isActive}
                  type={handleOfferType(item)}
                  chargebeeItemPriceId={item.product.chargebeeComponentId}
                  isLoading={fakeLoadingOnId === item.id}
                  theme="primary"
                  savingsPercent={calculateSavingsPercent(item.perAmount)}
                  isFeatured
                />
              </div>
            ))}
          </div>
        </div>

        {/* More Options Section */}
        <div className="p-6 pt-2">
          <h3 className="text-sm font-semibold text-neutral-600 mb-4 uppercase tracking-wide">
            More options
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {regularCards.map((item) => (
              <div key={item.id} className="flex flex-col">
                <CreditOfferCard
                  price={4}
                  id={item.id}
                  perAmount={item.perAmount}
                  totalAmount={item.totalAmount}
                  credits={item.credits}
                  isActive={item.isActive}
                  type={handleOfferType(item)}
                  chargebeeItemPriceId={item.product.chargebeeComponentId}
                  isLoading={fakeLoadingOnId === item.id}
                  theme="primary"
                  savingsPercent={calculateSavingsPercent(item.perAmount)}
                />
              </div>
            ))}
          </div>
        </div>
      </Box>
    </>
  );
};

export default BuyCreditsOfferPage;
